import { type App } from "vue"
import * as Sentry from "@sentry/vue"
// Importation des styles globaux
import "../style.css"
import "primeicons/primeicons.css"

import * as yup from "yup"
import { createPinia } from "pinia"
import servicePlugin from "@plugins/services.plugin"
import trackingPlugin from "@plugins/tracking.plugin"
import fontAwesomePlugin from "@plugins/fontawesome.plugin"
import routerPlugin from "@plugins/router.plugin"
import { createI18n } from "vue-i18n"

import {
    PRIMVEVUE_DIRECTIVES,
    PRIMVEVUE_SERVICE,
    HomeysTheme,
} from "./primevue"
import { SENTRY_DSN } from "@constant"
import PrimeVue from "primevue/config"
import ConfirmationService from "primevue/confirmationservice"
// import { useAuthStore } from "@stores/auth.store"

// composant globaux
import animateOnScroll from "@directives/animateOnScroll"

export function loadApp(app: App) {
    // Chargement des plugins
    loadPlugins(app)

    // Chargement des traductions Yup
    loadYupTranslation()

    // Chargement des composants PrimeVue
    loadPrimeVueComponents(app)

    // Chargement de sentry
    loadSentry(app)

    // si on est en mode DEV, on initilise le router directement sinon
    // on attend de recevoir le token
    app.use(routerPlugin)
    // if (mount) app.mount("#app")
    //if (import.meta.env.DEV) {
    //
    // } else {
    //     const tokenPromise = new Promise<string>((resolve) => {
    //         window.addEventListener("message", (event) => {
    //             if (event.data.type === "update:token" && event.data.token) {
    //                 resolve(event.data.token)
    //             }
    //         })
    //     })
    //     tokenPromise.then((token: string) => {
    //         const authStore = useAuthStore()
    //         authStore.setToken(token)
    //         app.use(routerPlugin)
    //         if (mount) app.mount("#app")
    //     })
    // }
}

/**
 * Charge les plugins globaux
 * @param app
 */
function loadPlugins(app: App) {
    app.use(createPiniaWithServices())

    yup.setLocale({
        mixed: {
            required: "Ce champ est obligatoire",
        },
        string: {
            email: "Veuillez saisir une adresse e-mail valide",
        },
    })

    app.use(fontAwesomePlugin)
    app.use(servicePlugin)
    app.use(trackingPlugin)
    loadI18n(app)
}

function loadI18n(app: App) {
    const i18n = createI18n({
        legacy: false,
        locale: "fr",
        messages: {
            fr: {
                page: {
                    residual_power: {
                        title: "Simulation IRVE",
                        error_building: ({
                            named,
                        }: {
                            named: (name: string) => string
                        }) =>
                            named("code") === "not_found_building"
                                ? "Le bâtiment n'a pas été trouvée"
                                : "Une erreur s'est produite lors du chargement du module de simulation. Veuillez réessayer plus tard.",
                    },
                },
            },
        },
        datetimeFormats: {
            fr: {
                short: {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                },
                long: {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    weekday: "short",
                    hour: "numeric",
                    minute: "numeric",
                },
            },
        },
    })

    app.use(i18n)
}

/**
 * Charge les composants PrimeVue
 * @param app - Instance de l'application Vue
 */
function loadPrimeVueComponents(app: App) {
    app.use(PrimeVue, HomeysTheme)
    app.use(ConfirmationService)

    Object.entries(PRIMVEVUE_DIRECTIVES).forEach(([keyName, definition]) => {
        app.directive(keyName, definition)
    })

    Object.values(PRIMVEVUE_SERVICE).forEach((service) => {
        app.use(service)
    })

    app.directive("animateonscrollv2", animateOnScroll)
}
/**
 * Charge les traductions Yup
 */
function loadYupTranslation() {
    yup.setLocale({
        mixed: {
            required: "Ce champ est obligatoire",
        },
        string: {
            email: "Veuillez saisir une adresse e-mail valide",
            min: "Veuillez saisir au moins ${min} caractères",
            max: "Veuillez saisir au plus ${max} caractères",
            matches: "Le format n'est pas correcte.",
        },
    })
}

/**
 * Crée une instance de Pinia avec les services injectés
 * @param app - Instance de l'application Vue
 * @returns Instance de Pinia
 */
function createPiniaWithServices() {
    const pinia = createPinia()
    return pinia
}

/**
 * Chargement de Sentry
 * @param app - Instance de l'application Vue
 */
function loadSentry(app: App) {
    Sentry.init({
        app,
        dsn: SENTRY_DSN,
        integrations: [],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for tracing.
        // We recommend adjusting this value in production
        tracesSampleRate: 0.25,
    })
}
